import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ProfileContext = createContext(null);
const STORAGE_KEY = "kinebe.active_profile_id";

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfiles = useCallback(async () => {
    if (!user || user === false) {
      setProfiles([]);
      setActiveProfile(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/profiles");
      setProfiles(data);
      const stored = localStorage.getItem(STORAGE_KEY);
      const found = data.find((p) => p.id === stored);
      setActiveProfile(found || null);
    } catch (err) {
      console.error("load profiles failed", err);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const selectProfile = (profile) => {
    setActiveProfile(profile);
    if (profile) localStorage.setItem(STORAGE_KEY, profile.id);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const createProfile = async (name, avatar_color) => {
    const { data } = await api.post("/profiles", { name, avatar_color });
    setProfiles([...profiles, data]);
    return data;
  };

  const updateProfile = async (id, patch) => {
    const { data } = await api.patch(`/profiles/${id}`, patch);
    setProfiles(profiles.map((p) => (p.id === id ? data : p)));
    if (activeProfile?.id === id) setActiveProfile(data);
    return data;
  };

  const deleteProfile = async (id) => {
    await api.delete(`/profiles/${id}`);
    setProfiles(profiles.filter((p) => p.id !== id));
    if (activeProfile?.id === id) {
      selectProfile(null);
    }
  };

  const uploadAvatar = async (id, file) => {
    const fd = new FormData();
    fd.append("image", file);
    const { data } = await api.post(`/profiles/${id}/avatar`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setProfiles(profiles.map((p) => (p.id === id ? data : p)));
    if (activeProfile?.id === id) setActiveProfile(data);
    return data;
  };

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        activeProfile,
        loading,
        selectProfile,
        createProfile,
        updateProfile,
        deleteProfile,
        uploadAvatar,
        reload: loadProfiles,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfiles = () => useContext(ProfileContext);
